import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
} from "@mui/material";
import { ArrowBack, Save } from "@mui/icons-material";
import { departmentService } from "../../services/departmentService";
import { useSnackbar } from "notistack";

export const DepartmentForm = ({ mode = "create", initialData = null }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(mode === "edit");

  const [formData, setFormData] = useState({
    departmentName: "",
    departmentCode: "",
    departmentType: "",
    location: {
      building: "",
      floor: "",
      room: "",
    },
    budgetCode: "",
    manager: "",
    contactPerson: "",
    contactPhone: "",
    contactEmail: "",
    isActive: true,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (mode === "edit" && id) {
      if (initialData) {
        populateForm(initialData);
        setFetchLoading(false);
      } else if (id) {
        fetchDepartment();
      }
    }
  }, [id, mode, initialData]);

  const fetchDepartment = async () => {
    try {
      setFetchLoading(true);
      const response = await departmentService.getDepartment(id);
      const responseData = response.data?.data || response.data;
      const department = responseData?.department || responseData;

      populateForm(department);
    } catch (error) {
      enqueueSnackbar("Error fetching department", { variant: "error" });
      navigate("/departments");
    } finally {
      setFetchLoading(false);
    }
  };

  const populateForm = (department) => {
    setFormData({
      departmentName: department.departmentName || "",
      departmentCode: department.departmentCode || "",
      departmentType: department.departmentType || "",
      location: {
        building: department.location?.building || "",
        floor: department.location?.floor || "",
        room: department.location?.room || "",
      },
      budgetCode: department.budgetCode || "",
      manager: department.manager?._id || "",
      contactPerson: department.contactPerson || "",
      contactPhone: department.contactPhone || "",
      contactEmail: department.contactEmail || "",
      isActive: department.isActive !== undefined ? department.isActive : true,
    });
  };

  const handleInputChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.departmentName.trim()) {
      newErrors.departmentName = "Department name is required";
    }

    if (!formData.departmentCode.trim()) {
      newErrors.departmentCode = "Department code is required";
    }

    if (!formData.departmentType) {
      newErrors.departmentType = "Department type is required";
    }

    if (!formData.contactPhone.trim()) {
      newErrors.contactPhone = "Contact phone is required";
    }

    if (
      formData.contactEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)
    ) {
      newErrors.contactEmail = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      enqueueSnackbar("Please fix the errors in the form", {
        variant: "error",
      });
      return;
    }

    setLoading(true);

    try {
      // Clean up the data before sending
      const submitData = { ...formData };

      // Remove manager field if it's empty (backend expects ObjectId or nothing)
      if (!submitData.manager || submitData.manager.trim() === "") {
        delete submitData.manager;
      }

      // Remove empty optional fields
      if (!submitData.budgetCode || submitData.budgetCode.trim() === "") {
        delete submitData.budgetCode;
      }

      if (!submitData.contactEmail || submitData.contactEmail.trim() === "") {
        delete submitData.contactEmail;
      }

      if (!submitData.contactPerson || submitData.contactPerson.trim() === "") {
        delete submitData.contactPerson;
      }

      if (mode === "edit") {
        await departmentService.updateDepartment(id, submitData);
        enqueueSnackbar("Department updated successfully!", {
          variant: "success",
        });
      } else {
        await departmentService.createDepartment(submitData);
        enqueueSnackbar("Department created successfully!", {
          variant: "success",
        });
      }
      navigate("/hospital/hospital-inventory/department");
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        `Error ${mode === "edit" ? "updating" : "creating"} department`;
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/hospital/hospital-inventory/department")}
          variant="outlined"
        >
          Back to Departments
        </Button>
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>
          {mode === "edit" ? "Edit Department" : "Add New Department"}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Basic Information
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Department Name"
                      value={formData.departmentName}
                      onChange={(e) =>
                        handleInputChange("departmentName", e.target.value)
                      }
                      required
                      error={!!errors.departmentName}
                      helperText={errors.departmentName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Department Code"
                      value={formData.departmentCode}
                      onChange={(e) =>
                        handleInputChange("departmentCode", e.target.value)
                      }
                      required
                      error={!!errors.departmentCode}
                      helperText={errors.departmentCode}
                      disabled={mode === "edit"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="Department Type"
                      value={formData.departmentType}
                      onChange={(e) =>
                        handleInputChange("departmentType", e.target.value)
                      }
                      required
                      error={!!errors.departmentType}
                      helperText={errors.departmentType}
                    >
                      <MenuItem value="production">Production</MenuItem>
                      <MenuItem value="warehouse">Warehouse</MenuItem>
                      <MenuItem value="maintenance">Maintenance</MenuItem>
                      <MenuItem value="administration">Administration</MenuItem>
                      <MenuItem value="research">Research</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Budget Code"
                      value={formData.budgetCode}
                      onChange={(e) =>
                        handleInputChange("budgetCode", e.target.value)
                      }
                      helperText="Optional budget tracking code"
                    />
                  </Grid>
                  {mode === "edit" && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        label="Status"
                        value={formData.isActive? "true":"false"}
                        onChange={(e) =>
                          handleInputChange(
                            "isActive",
                            e.target.value === "true"
                          )
                        }
                      >
                        <MenuItem value="true">Active</MenuItem>
                        <MenuItem value="false">Inactive</MenuItem>
                      </TextField>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Location Information */}
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Location Information
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Building"
                      value={formData.location.building}
                      onChange={(e) =>
                        handleInputChange("location.building", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Floor"
                      value={formData.location.floor}
                      onChange={(e) =>
                        handleInputChange("location.floor", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Room"
                      value={formData.location.room}
                      onChange={(e) =>
                        handleInputChange("location.room", e.target.value)
                      }
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Contact Information
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      value={formData.contactPerson}
                      onChange={(e) =>
                        handleInputChange("contactPerson", e.target.value)
                      }
                      helperText="Primary contact person for this department"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Contact Phone"
                      value={formData.contactPhone}
                      onChange={(e) =>
                        handleInputChange("contactPhone", e.target.value)
                      }
                      required
                      error={!!errors.contactPhone}
                      helperText={errors.contactPhone}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="email"
                      label="Contact Email"
                      value={formData.contactEmail}
                      onChange={(e) =>
                        handleInputChange("contactEmail", e.target.value)
                      }
                      error={!!errors.contactEmail}
                      helperText={errors.contactEmail}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Manager Information */}
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Manager Information
                </Typography>

                <Alert severity="info" sx={{ mb: 2 }}>
                  Manager assignment will be available after department
                  creation. You can assign a manager from the department details
                  page.
                </Alert>

                <TextField
                  fullWidth
                  label="Manager"
                  value={formData.manager}
                  disabled
                  helperText="Assign manager from department details page"
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Submit Button */}
        <Box
          sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 4 }}
        >
          <Button
            onClick={() => navigate("/hospital/hospital-inventory/department")}
            variant="outlined"
            size="large"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            startIcon={<Save />}
          >
            {loading
              ? mode === "edit"
                ? "Updating..."
                : "Creating..."
              : mode === "edit"
              ? "Update Department"
              : "Create Department"}
          </Button>
        </Box>
      </form>
    </Box>
  );
};
